import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EProcessSharing, EProcessStatus, IActivityDto, IProcessDto } from 'core/dto';
import { mapKey, subjectFromObs } from 'core/rx-js';
import { ScreenUtilsService } from 'core/services';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { statusDisplays } from 'projects/designer/src/app/utils/status-display';
import { filter, map, Subject, take } from 'rxjs';
import { IFieldInfo } from './process-review-field/process-review-field.component';
import { ProcessReviewService } from './process-review.service';

@Component({
  selector: 'ss-process-review',
  templateUrl: './process-review.component.html',
  styleUrls: ['./process-review.component.scss'],
  providers: [ProcessReviewService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessReviewComponent {
  readonly #destroy = new Subject<void>()
  readonly processIdSubject = this._processReviewService.processIdSubject;
  @Input() get processId() {
    return this.processIdSubject.value;
  }
  set processId(value) {
    this.processIdSubject.next(value)
  }

  @Input() showTitle = true;


  @Output()
  readonly processSubject = this._processReviewService.processSubject;
  readonly activitiesSubject = subjectFromObs({
    until: this.#destroy,
    observable: this._processReviewService.activitiesSubject.pipe(
      filter(Boolean),
      map(activities => activities.sort((prev, next) => prev.orderIndex - next.orderIndex))
    ),
    initialValue: null
  })
  readonly tasksSubject = this._processReviewService.tasksSubject;
  readonly pdfDocumentsSubject = this._processReviewService.pdfDocumentsSubject;

  readonly creatorDisplayObs = this.processSubject.pipe(
    filter(Boolean),
    map(({ creator }) => creator),
    filter(Boolean),
    map(creator => creator.firstName + ' ' + creator.lastName)
  )

  readonly smallScreenObs = this._screenUtilsService.screenSizeSubject.pipe(
    map((size) => size === 'sm' || size === 'xs')
  )

  readonly processSharingDisplays: Record<EProcessSharing, DsgDeepTranslationKey> = {
    [EProcessSharing.creatorSigners]: 'ss-entities.enum-process-sharing.creator-signers',
    [EProcessSharing.creatorSignersTeams]: 'ss-entities.enum-process-sharing.creator-signers-teams',
  }

  readonly subtitleDisplayObs = this.processSubject.pipe(
    filter(Boolean),
    map((process): Partial<Record<keyof IProcessDto, IFieldInfo>> => ({
      creator: { label: 'ss-entities.process.creator', value: process.creator, display: process.creator?.firstName + ' ' + process.creator?.lastName },
      created: { label: 'ss-entities.process.created', value: process.created, display: formatDate(process.created, 'dd/MM/yyyy, HH:mm', 'en-us') },
      status: { label: 'ss-entities.process.status', value: process.status, display: this.getStatusDisplay(process.status) },

      validTill: { label: 'ss-entities.process.valid-until', value: process.validTill, display: formatDate(process.validTill, 'dd/MM/yyyy, HH:mm', 'en-us') },
      note: { label: 'ss-entities.process.note', value: process.note, display: process.note },
      processSharingType: { label: 'ss-entities.process.process-sharing', value: process.processSharingType, display: this.processSharingDisplays[process.processSharingType] },
      statusChanged: {
        label: 'ss-entities.process.status-changed',
        value: process.statusChanged,
        display: formatDate(process.statusChanged, 'dd/MM/yyyy, HH:mm', 'en-us'),
        hidden: ![EProcessStatus.Cancelled, EProcessStatus.Finished].includes(process.status)
      },
      statusChangedReason: {
        label: 'ss-entities.activity.reject.reason',
        value: process.statusChangedReason,
        display: process.statusChangedReason,
        hidden: process.status !== EProcessStatus.Rejected
      }
    })
    )
  )


  getStatusDisplay(status: EProcessStatus) {
    return statusDisplays[status];
  }

  constructor(
    private readonly _processReviewService: ProcessReviewService,
    private readonly _translateService: TranslateService,
    private readonly _screenUtilsService: ScreenUtilsService
  ) { }


  shallowClone(activity: IActivityDto) {
    return { ...activity }
  }

  getFieldInfoVisibleObs(field: keyof IProcessDto) {

    return this.getFieldInfoObs(field).pipe(
      map(field => !field.hidden && field.value)
    )
  }

  getFieldInfoObs(field: keyof IProcessDto) {

    return this.subtitleDisplayObs.pipe(
      mapKey(field),
      take(1)
    )
  }

  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }
}
